import type { SummaryCard } from "../types";

import { SkeletonLoader } from "../../SkeletonLoader";
import { Typo } from "../../Typo";
import { usePerformanceReportTranslations } from "../hooks/usePerformanceReportTranslations";

/**
 * Line height (px) of the `labelSM` card label. Used to reserve the label row
 * when the label isn't known — on a cold error there's no API response to take
 * labels from, and the cards must still line up with the loaded layout.
 */
const LABEL_HEIGHT = 21;

/**
 * Row of headline-metric cards (net-new card design, intended for reuse across
 * reports). Light blue-grey surface, no border, unit rendered as smaller
 * secondary text. Reflows 5 → 3 → 2 columns on narrower viewports (AC-13).
 *
 * The three non-data states render the same card row rather than disappearing:
 * loading shows skeleton bars, empty forces `0` (keeping the USD unit), and
 * error shows a disabled em-dash with no unit.
 */
export const SummaryCards = ({
  cards,
  isLoading,
  isError,
  isEmpty,
  skeletonCount = 5,
}: {
  cards: SummaryCard[];
  isLoading?: boolean;
  /** Values become a disabled `—` and the unit is dropped. */
  isError?: boolean;
  /** Values become `0`; USD cards keep their unit. */
  isEmpty?: boolean;
  /** How many placeholder cards to show on the very first load, before the
   * API has ever returned (so labels aren't known yet either). */
  skeletonCount?: number;
}) => {
  const { summaryCardLabels } = usePerformanceReportTranslations();

  // On the very first load — or a first-load error — there's no data at all, so
  // `cards` is empty and there's nothing to map over. Fall back to blank
  // placeholder slots so the card row still shows instead of vanishing.
  const items: (SummaryCard | undefined)[] =
    cards.length > 0
      ? cards
      : isLoading || isError || isEmpty
        ? Array.from({ length: skeletonCount })
        : [];

  return (
    <div className="grid grid-cols-2 gap-6 md:grid-cols-3 xl:grid-cols-5">
      {items.map((card, index) => (
        <div
          key={card?.translationKey ?? index}
          className="flex flex-col gap-2 rounded-2xl bg-surface-brand-subtle p-6"
        >
          {card ? (
            <Typo element="span" type="labelSM" textColor="text-text-secondary">
              {summaryCardLabels[card.translationKey]}
            </Typo>
          ) : isLoading ? (
            <SkeletonLoader
              variant="rounded"
              motionReduce
              width={64}
              height={LABEL_HEIGHT}
              radius="lg"
            />
          ) : (
            <div aria-hidden="true" style={{ height: LABEL_HEIGHT }} />
          )}
          {isLoading ? (
            <SkeletonLoader
              variant="rounded"
              motionReduce
              width={72}
              height={29}
              radius="lg"
            />
          ) : (
            <Typo
              element="span"
              type="headingXL"
              textColor={isError ? "text-text-disabled" : "text-text-primary"}
            >
              {isError ? "—" : isEmpty ? "0" : card?.value}
              {/* An errored card shows only the dash — a unit next to it would
                  read as a real, zeroed measurement. */}
              {!isError && card?.unit && (
                <Typo
                  element="span"
                  type="labelSM"
                  textColor="text-text-secondary"
                  className="ml-1"
                >
                  {card.unit}
                </Typo>
              )}
            </Typo>
          )}
        </div>
      ))}
    </div>
  );
};
